import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type { LeaseTemplateType } from '~/swagger/portal/Api';

const SIGNING_TEMPLATE_URL = '/api/signing-templates';

export interface SigningDocumentTemplateItemDto {
  id: string;
  name?: string | null;
  templateType: LeaseTemplateType;
  leaseTemplateCategoryId?: string | null;
  documentId: string;
  created: string;
  updated: string;
}

export interface SigningDocumentTemplateDto extends SigningDocumentTemplateItemDto {
  companyId: string;
  docxUrl: string;
}

export interface SigningTemplateUploadSessionDto {
  sessionId: string;
  endpoint: string;
  method: string;
  fields: Record<string, string>;
  expires: string;
}

export interface RequestSigningTemplateUploadSessionDto {
  fileName: string;
  fileSize: number;
  contentType: string;
}

export interface CreateSigningTemplateDto {
  name?: string;
  templateType: LeaseTemplateType;
  leaseTemplateCategoryId: string;
  uploadSessionId?: string;
}

export interface ReplaceSigningTemplateFileDto {
  uploadSessionId: string;
  ifMatchToken?: string;
}

export interface ReplaceSigningTemplateFileResultDto {
  token: string;
  docxUrl: string;
}

export interface SigningTemplateKeyDto {
  key: string;
  name: string;
  description?: string | null;
  group: string;
  kind: string;
}

export const SIGNING_TEMPLATE_DOCX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export class SigningDocumentTemplateRepository extends BaseRestRepository {
  public async getSigningTemplates(categoryId?: string): Promise<SigningDocumentTemplateItemDto[]> {
    const url = urlWithQuery(SIGNING_TEMPLATE_URL, categoryId ? { categoryId } : undefined);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getSigningTemplate(id: string): Promise<SigningDocumentTemplateDto> {
    const response = await this.fetchWithAuth(`${SIGNING_TEMPLATE_URL}/${id}`);
    return this.getJsonResponse(response);
  }

  public async getSigningTemplateKeys(templateType: `${LeaseTemplateType}`): Promise<SigningTemplateKeyDto[]> {
    const url = urlWithQuery(`${SIGNING_TEMPLATE_URL}/keys`, { templateType });
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async requestUploadSession(payload: RequestSigningTemplateUploadSessionDto): Promise<SigningTemplateUploadSessionDto> {
    const response = await this.fetchWithAuth(`${SIGNING_TEMPLATE_URL}/upload-sessions`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  /** POST multipart to the storage provider (e.g. S3 presigned). No auth header — uses session fields only. */
  public async uploadBlobToSession(session: SigningTemplateUploadSessionDto, file: File): Promise<void> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(session.fields)) {
      formData.append(key, value);
    }
    formData.append('file', file);

    const response = await this.fetchWithoutAuth(session.endpoint, {
      method: session.method || 'POST',
      body: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }
  }

  public async createSigningTemplate(payload: CreateSigningTemplateDto): Promise<SigningDocumentTemplateDto> {
    const response = await this.fetchWithAuth(SIGNING_TEMPLATE_URL, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async replaceFile(id: string, payload: ReplaceSigningTemplateFileDto): Promise<ReplaceSigningTemplateFileResultDto> {
    const body: Record<string, unknown> = { uploadSessionId: payload.uploadSessionId };
    if (payload.ifMatchToken) body.ifMatchToken = payload.ifMatchToken;

    const response = await this.fetchWithAuth(`${SIGNING_TEMPLATE_URL}/${id}/file`, { method: 'POST', body: JSON.stringify(body) }, false);
    const raw = (await this.getJsonResponse(response)) as Record<string, unknown>;
    return {
      token: String(raw.token ?? raw.Token ?? ''),
      docxUrl: String(raw.docxUrl ?? raw.DocxUrl ?? ''),
    };
  }

  public async deleteSigningTemplate(id: string): Promise<void> {
    await this.fetchWithAuth(`${SIGNING_TEMPLATE_URL}/${id}`, { method: 'DELETE' });
  }
}

export const signingDocumentTemplateRepo = new SigningDocumentTemplateRepository();
